/** @file Cut slots of a two-point part: which edge loses how many whole texels (design 21.6 step 5). */
import type { CutMode } from '@zilo/pose3d-format/types/facet';
import { twoPointFrameOf } from '@zilo/pose3d-render-core/corners/twoPointFrameOf';
import { anchorDirOf } from '@zilo/pose3d-render-core/cut/anchorDirOf';
import { cutTexelsOf } from '@zilo/pose3d-render-core/cut/cutTexelsOf';
import { edgeDistanceOf } from '@zilo/pose3d-render-core/cut/edgeDistanceOf';
import { pivot2Of } from '@zilo/pose3d-render-core/cut/pivot2Of';
import { TILE_EDGES } from '@zilo/pose3d-render-core/cut/tileEdges';

/** The cut slots: `cutMode` 0 none, 1 cut, 2 scale; the cut line `cutAngle` (edge normal) at `cutPos` from the pivot. */
export type CutParams = { cutMode: 0 | 1 | 2; cutAngle: number; cutPos: number };

/** The tile, the two screen points and the pivot2 inset `cutOff` (`anchor.pixelOffset`, CR-004) a cut reads. */
export type CutInput = {
  cut: CutMode; inverse: boolean; pivot: [number, number]; size: [number, number]; drawnScreenAngleDeg: number;
  aScreen: [number, number]; bScreen: [number, number]; viewport: [number, number]; cutOff: number;
};

/**
 * `scale`: cutMode 2, nothing cut. `cut`: pivot2's exit edge (or the opposite edge with `cutInverse`) loses
 * `c = round(T (1 - ratio))` whole texels when the W-weighted ratio is below 1 (T = the tile extent across that edge):
 * `cutAngle` = the edge's outward normal, `cutPos` = pivot-to-edge distance minus c. `none`: neutral.
 */
export function cutSlotsOf(i: CutInput): CutParams {
  if (i.cut === 'none') return { cutMode: 0, cutAngle: 0, cutPos: 0 };
  if (i.cut === 'scale') return { cutMode: 2, cutAngle: 0, cutPos: 0 };
  const [w, h] = i.size;
  const p2 = pivot2Of(i.pivot, anchorDirOf(i.drawnScreenAngleDeg), w, h, i.cutOff);
  const { ratio } = twoPointFrameOf(i.aScreen, i.bScreen, i.pivot, p2.point, i.viewport);
  const edge = i.inverse ? TILE_EDGES[p2.edge].opposite : p2.edge;
  const c = cutTexelsOf(TILE_EDGES[edge].axis === 0 ? w : h, ratio);
  return { cutMode: 1, cutAngle: TILE_EDGES[edge].normalDeg, cutPos: edgeDistanceOf(edge, i.pivot, w, h) - c };
}
